import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, RotateCcw, MessageCircle } from "lucide-react";
import { BlogLayout } from "@/pages/blog/BlogLayout";
import { useSeo } from "@/pages/blog/useSeo";
import { MaskLine, Reveal } from "@/components/landing/Reveal";
import { WHATSAPP_URL } from "@/components/landing/data";
import { QUIZ, RESULTS, resultFor } from "@/content/quiz";

const Question = ({ step, onAnswer, onBack }) => {
  const item = QUIZ[step];
  return (
    <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} data-testid={`quiz-question-${step}`}>
      <p className="f-mono text-xs text-[#3E6B48]">Domanda {step + 1} di {QUIZ.length}</p>
      <h2 className="f-serif text-3xl sm:text-4xl text-[#1B3B28] leading-tight mt-4">{item.q}</h2>
      <div className="mt-8 grid gap-3">
        {item.a.map(([label, v], i) => (
          <button key={label} onClick={() => onAnswer(v)} data-testid={`quiz-answer-${step}-${i}`}
            className="group flex items-center justify-between text-left rounded-2xl border border-[#1B3B28]/15 px-5 py-4 text-[#1B3B28] hover:bg-[#1B3B28] hover:text-[#FCFBF7] hover:border-[#1B3B28] active:scale-[0.99] transition-[background-color,color,border-color,transform] duration-300">
            <span className="text-base">{label}</span>
            <ArrowUpRight size={16} className="opacity-40 group-hover:opacity-100 group-hover:rotate-45 transition-transform duration-300" />
          </button>
        ))}
      </div>
      {step > 0 && (
        <button onClick={onBack} data-testid="quiz-back-btn" className="mt-6 inline-flex items-center gap-2 text-sm text-[#526356] hover:text-[#1B3B28]">
          <ArrowLeft size={14} /> Domanda precedente
        </button>
      )}
    </motion.div>
  );
};

const Result = ({ level, onRestart }) => {
  const r = RESULTS[level];
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="quiz-result">
      <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${level === "basso" ? "bg-[#86C89B]/40 text-[#1B3B28]" : level === "moderato" ? "bg-[#EBD9A8] text-[#5A4410]" : "bg-[#F1D3C8] text-[#7A2E1C]"}`} data-testid="quiz-result-level">{r.label}</span>
      <h2 className="f-serif text-4xl sm:text-5xl text-[#1B3B28] leading-[1.02] mt-5" data-testid="quiz-result-title">{r.title}</h2>
      <p className="text-[#526356] leading-relaxed mt-5">{r.text}</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to={r.cta.to} data-testid="quiz-result-cta" className="inline-flex items-center gap-2 rounded-full bg-[#1B3B28] text-[#FCFBF7] px-6 py-3.5 text-sm hover:bg-[#3E6B48] transition-colors">{r.cta.label} <ArrowUpRight size={15} /></Link>
        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="quiz-result-whatsapp" className="inline-flex items-center gap-2 rounded-full border border-[#1B3B28]/20 text-[#1B3B28] px-6 py-3.5 text-sm hover:bg-[#E6ECE4] transition-colors"><MessageCircle size={15} /> Chiedi su WhatsApp</a>
      </div>
      <button onClick={onRestart} data-testid="quiz-restart-btn" className="mt-6 inline-flex items-center gap-2 text-sm text-[#526356] hover:text-[#1B3B28]"><RotateCcw size={14} /> Rifai il test</button>
      <p className="text-xs text-[#526356] mt-8 leading-relaxed">Il test è uno strumento orientativo e non è una diagnosi: solo una visita con sondaggio parodontale può valutare con certezza la salute delle gengive.</p>
    </motion.div>
  );
};

const Quiz = () => {
  const [answers, setAnswers] = useState([]);
  const step = answers.length;
  const done = step === QUIZ.length;
  const answer = (v) => {
    const next = [...answers, v];
    setAnswers(next);
    if (next.length === QUIZ.length) window.gtag?.("event", "quiz_complete", { result: resultFor(next) });
  };
  return (
    <div className="rounded-[2rem] bg-[#FCFBF7] border border-[#1B3B28]/10 p-7 sm:p-12 shadow-[0_30px_80px_-40px_rgba(27,59,40,0.35)] min-h-[520px]" data-testid="quiz-card">
      <div className="h-1.5 rounded-full bg-[#E6ECE4] overflow-hidden mb-10" aria-hidden="true">
        <motion.div className="h-full bg-[#3E6B48]" animate={{ width: `${(step / QUIZ.length) * 100}%` }} transition={{ duration: 0.5 }} />
      </div>
      {done ? <Result level={resultFor(answers)} onRestart={() => setAnswers([])} />
        : <Question step={step} onAnswer={answer} onBack={() => setAnswers((a) => a.slice(0, -1))} />}
    </div>
  );
};

export default function QuizPage() {
  useSeo({
    title: "Test: le tue gengive sono a rischio parodontite?",
    description: "Un test gratuito di 8 domande per capire se le tue gengive mostrano segnali di gengivite o parodontite. Risultato immediato e consigli del Dott. Nicola Mauro, Centro Dentistico Galliera.",
    path: "/test-gengive",
  });
  return (
    <BlogLayout>
      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-36 sm:pt-44 pb-28 grid lg:grid-cols-12 gap-12 items-start" data-testid="quiz-page">
        <div className="lg:col-span-5 lg:sticky lg:top-36">
          <p className="eyebrow text-[#3E6B48]">Test di autovalutazione · 1 minuto</p>
          <h1 className="f-serif text-5xl sm:text-6xl text-[#1B3B28] mt-5 leading-[0.98]" data-testid="quiz-title">
            <MaskLine>Le tue gengive</MaskLine>
            <MaskLine delay={0.12}><em className="text-[#3E6B48]">sono a rischio?</em></MaskLine>
          </h1>
          <Reveal delay={0.3}>
            <p className="text-lg text-[#526356] mt-8 leading-relaxed max-w-md">
              Otto domande semplici, nessun dato personale. Alla fine scopri il tuo livello di rischio e cosa ti consigliamo di fare.
            </p>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-7" delay={0.15}><Quiz /></Reveal>
      </section>
    </BlogLayout>
  );
}
